import { Injectable } from '@angular/core';
import { lastValueFrom, timeout } from 'rxjs';
import { API_ENDPOINT, DEVICE_LOGIN_HEADERS, GLOBAL_DATA_KEY, LOCAL_STORAGE_KEY } from '@core/configs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApiService } from '../common/api.service';
import { FirebaseDeviceInfoService } from '../common/firebase/firebase-device-info.service';
import { GlobalDataService, LocalStorageService, SessionContextService } from '../common';
import { Mode } from '@core/constants';
import { environment } from '@environments/environment';

export interface DeviceInfo {
    deviceName: string;
    webViewVersion: string;
}

export const EMPTY_DEVICE_INFO: DeviceInfo = {
    deviceName: '',
    webViewVersion: '',
};

interface DeviceLoginAndroidBridge {
    getDeviceLoginToken?: () => string;
    getDeviceMemory?: () => string;
}

@Injectable({ providedIn: 'root' })
export class DeviceLicensesService {
    private readonly deviceLoginSaveTimeoutMs = 20000;
    private settingConfigRequest: Promise<void> = Promise.resolve();
    private lastMetadataSignature = '';

    constructor(
        private readonly http: HttpClient,
        private readonly apiService: ApiService,
        private readonly firebaseDeviceInfoService: FirebaseDeviceInfoService,
        private readonly sessionContextService: SessionContextService,
        private readonly globalDataService: GlobalDataService,
        private readonly localStorageService: LocalStorageService,
    ) { }

    /**
     * Đăng ký/cập nhật tên device theo deviceId (TV/WebView).
     * API tự động insert nếu chưa có record, update nếu đã có -> gọi vô điều kiện, không cần check tồn tại trước.
     */
    async updateDeviceNameByDeviceId(deviceId: string, deviceName: string): Promise<void> {
        const url = `${API_ENDPOINT.deviceLicenses.updateNameByDeviceId}`;
        const headers = this.createDeviceLoginHeaders(deviceId);
        if (!headers) {
            return;
        }
        const body = { deviceId, deviceName };
        try {
            await lastValueFrom(this.http.post(url, body, { headers }));
        } catch {}
    }

    async getLogEnabledByDeviceId(deviceId: string): Promise<number> {
        if (!deviceId) return 0;
        const headers = this.createDeviceLoginHeaders(deviceId);
        if (!headers) return 0;
        
        try {
            const res: any = await lastValueFrom(this.http.post(API_ENDPOINT.deviceLicenses.getLogEnabledByDeviceId, { deviceId }, { headers }));
            return res?.logEnabled === 1 ? 1 : 0;
        } catch (error) {
            console.error('getLogEnabledByDeviceId failed:', error);
            return 0;
        }
    }

    async updateLogEnableOffice(): Promise<number | null> {
        const deviceId = this.firebaseDeviceInfoService.getAndroidDeviceId();
        if (!deviceId) return null;

        try {
            const res = await lastValueFrom(this.apiService.post<any>(
                API_ENDPOINT.deviceLicenses.syncLogEnabledByDeviceId,
                { deviceId },
            ));
            const logEnabled = res?.result?.logEnabled;
            return logEnabled === 0 || logEnabled === 1 ? logEnabled : null;
        } catch (error) {
            console.error('updateLogEnableOffice failed:', error);
            return null;
        }
    }

    async applyLogEnabled(logEnabled: number): Promise<boolean> {
        const deviceId = this.firebaseDeviceInfoService.getAndroidDeviceId();
        const headers = this.createDeviceLoginHeaders(deviceId);
        if (!deviceId || !headers || (logEnabled !== 0 && logEnabled !== 1)) return false;

        try {
            await lastValueFrom(this.http.post(
                API_ENDPOINT.deviceLicenses.applyLogEnabledByDeviceId,
                { deviceId, logEnabled },
                { headers },
            ));
            return true;
        } catch (error) {
            return false;
        }
    }

    async syncDeviceMetadata(force = false): Promise<boolean> {
        const deviceId = this.firebaseDeviceInfoService.getAndroidDeviceId();
        if (!deviceId) return false;

        const appVersion = this.firebaseDeviceInfoService.getAppVersion();
        const webViewVersion = this.firebaseDeviceInfoService.getWebViewVersion();
        const body = {
            deviceId,
            deviceName: await this.getDeviceName(deviceId),
            userAgent: navigator.userAgent,
            appVersion,
            webViewVersion,
            memoryTotalBytes: this.getMemoryTotalBytes()
        };
        const signature = JSON.stringify(body);
        if (!force && signature === this.lastMetadataSignature) return true;

        try {
            await lastValueFrom(this.apiService.post(API_ENDPOINT.deviceLicenses.updateMetadataByDeviceId, body));
            this.lastMetadataSignature = signature;
            return true;
        } catch (error) {
            console.error('syncDeviceMetadata failed:', error);
            return false;
        }
    }

    /**
     * Only runs in TV mode when an androidDeviceId is available; context
     * (office/corpPrefix) is obtained from the current session/global state.
     */
    async updateLoginCustomerInfoByDeviceId(accessToken: string): Promise<void> {
        try {
            const mode = this.localStorageService.getItem<string | number>(LOCAL_STORAGE_KEY.mode);
            if (Number(mode) !== Mode.TV) {
                return;
            }

            const deviceId = this.firebaseDeviceInfoService.getAndroidDeviceId();
            if (!deviceId) {
                return;
            }

            const officeSelected = this.sessionContextService.getOfficeSelected<any>();
            const corpPrefix = this.sessionContextService.getCorpPrefix()
                || this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix)
                || '';
            const officeCd = officeSelected?.officeCd ?? '';
            const corpOffice = corpPrefix && officeCd ? `corp_${corpPrefix}#office_${officeCd}` : '';
            const savedLogInStaffId = this.sessionContextService.getStaffInfo<any>()?.logInStaffId;
            const body = {
                deviceId,
                token: accessToken ?? '',
                companyId: officeSelected?.companyId != null ? String(officeSelected.companyId) : '',
                corpOffice,
                logInStaffId: typeof savedLogInStaffId === 'string' ? savedLogInStaffId : '',
            };
            await lastValueFrom(this.apiService.post(
                API_ENDPOINT.deviceLicenses.updateLoginCustomerInfoByDeviceId,
                body,
            ));
        } catch (error) {
            console.error('updateLoginCustomerInfoByDeviceId failed: ', error);
        }
    }

    /**
     * Persists device login data after staff login when an office has not been
     * selected yet. The empty office segment preserves the selected corporation
     * for the next device login and is replaced after office selection.
     */
    async updateLoginCustomerInfoBeforeOfficeByDeviceId(accessToken: string): Promise<void> {
        try {
            const mode = this.localStorageService.getItem<string | number>(LOCAL_STORAGE_KEY.mode);
            if (Number(mode) !== Mode.TV) {
                return;
            }

            const deviceId = this.firebaseDeviceInfoService.getAndroidDeviceId();
            const headers = this.createDeviceLoginHeaders(deviceId);
            const staffInfo = this.sessionContextService.getStaffInfo<any>();
            const companyId = staffInfo?.stfBelongs?.[0]?.companyId;
            const corpPrefix = this.sessionContextService.getCorpPrefix()
                || this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix)
                || '';

            if (!deviceId || !headers || companyId == null || !corpPrefix) {
                return;
            }

            await lastValueFrom(
                this.http.post(
                    API_ENDPOINT.deviceLicenses.saveLoginCustomerInfoBeforeOfficeByDeviceId,
                    {
                        deviceId,
                        token: accessToken ?? '',
                        companyId: String(companyId),
                        corpOffice: `corp_${corpPrefix}#office_`,
                        logInStaffId: typeof staffInfo?.logInStaffId === 'string' ? staffInfo.logInStaffId : '',
                    },
                    { headers },
                ).pipe(timeout({ first: this.deviceLoginSaveTimeoutMs })),
            );
        } catch (error) {
            console.error('updateLoginCustomerInfoBeforeOfficeByDeviceId failed: ', error);
        }
    }

    /**
     * The native app owns this fixed opaque token. The web app only reads it
     * and forwards it with authenticated device requests.
     */
    getDeviceLoginToken(): string {
        try {
            if (!this.isReloginSupported(this.firebaseDeviceInfoService.getAppVersion())) {
                return '';
            }
            const androidBridge = this.getDeviceLoginAndroidBridge();
            const token = typeof androidBridge?.getDeviceLoginToken === 'function'
                ? androidBridge.getDeviceLoginToken()
                : '';
            return typeof token === 'string' ? token.trim() : '';
        } catch {
            return '';
        }
    }

    createDeviceLoginHeaders(deviceId: string, token = this.getDeviceLoginToken()): HttpHeaders | null {
        if (!token) {
            return null;
        }

        return new HttpHeaders({
            [DEVICE_LOGIN_HEADERS.deviceId]: deviceId,
            [DEVICE_LOGIN_HEADERS.token]: token,
        });
    }

    private isReloginSupported(appVersion: string): boolean {
        const [majorVersion = 0, minorVersion = 0] = appVersion.split('.').map(Number);
        if (majorVersion > 1) {
            return true;
        }

        const minimumMinorVersion = environment.env === 'prod' ? 3 : 1;
        return minorVersion > minimumMinorVersion;
    }

    async clearLoginCustomerInfoByDeviceId(): Promise<void> {
        const deviceId = this.getTvDeviceId();
        try {
            const headers = this.createDeviceLoginHeaders(deviceId);
            if (!deviceId || !headers) {
                return;
            }

            await lastValueFrom(this.http.post(
                API_ENDPOINT.deviceLicenses.clearLoginCustomerInfoByDeviceId,
                { deviceId },
                { headers },
            ));
        } catch (error) {
            console.error('clearLoginCustomerInfoByDeviceId failed:', error);
        }
    }

    /**
     * Lấy tên device đã lưu trong DynamoDB theo deviceId (TV/WebView).
     * API yêu cầu Authorize (khác updateNameByDeviceId), trả 404 nếu deviceId chưa có record.
     */
    async getDeviceInfoByDeviceId(deviceId: string): Promise<DeviceInfo>{
        const url = `${API_ENDPOINT.deviceLicenses.getNameByDeviceId}`;
        const body = { deviceId: deviceId };
        const headers = this.createDeviceLoginHeaders(deviceId);
        if (!headers) return EMPTY_DEVICE_INFO;
        try {
            const res: any = await lastValueFrom(this.http.post(url, body, { headers }));
            return {
                deviceName: res?.deviceName ?? '',
                webViewVersion: res?.webViewVersion ?? '',
            };
        } catch { return EMPTY_DEVICE_INFO; }
    }

    // device name is only taken from DynamoDB
    async getDeviceName(deviceId: string): Promise<string> {
        return (await this.getDeviceInfoByDeviceId(deviceId)).deviceName;
    }

    async syncSettingConfig(): Promise<void> {
        const settingConfigPayload = this.getCurrentTvSettingConfigPayload();
        if (!settingConfigPayload) {
            return;
        }
        settingConfigPayload.deviceName = await this.getDeviceName(settingConfigPayload.deviceId);

        return this.queueSettingConfigRequest(
            API_ENDPOINT.deviceLicenses.updateSettingConfigByDeviceId,
            settingConfigPayload,
            'updateSettingConfigByDeviceId',
        );
    }

    private queueSettingConfigRequest(
        url: string,
        settingConfigPayload: { deviceId: string; settingConfig: string; deviceName: string; corpOffice: string },
        operationName: string,
    ): Promise<void> {
        this.settingConfigRequest = this.settingConfigRequest.then(async () => {
            try {
                await lastValueFrom(this.apiService.post(url, settingConfigPayload));
            } catch (error) {
                console.error(`${operationName} failed:`, error);
            }
        });

        return this.settingConfigRequest;
    }

    private getCurrentTvSettingConfigPayload(): { deviceId: string; settingConfig: string; deviceName: string; corpOffice: string } | null {
        const mode = this.localStorageService.getItem<string | number>(LOCAL_STORAGE_KEY.mode);
        if (Number(mode) !== Mode.TV) {
            console.warn('Setting config sync skipped: TV mode is not active.', { mode });
            return null;
        }

        const deviceId = this.firebaseDeviceInfoService.getAndroidDeviceId();
        const settingConfig = localStorage.getItem(LOCAL_STORAGE_KEY.settingDialog);
        const deviceName = this.firebaseDeviceInfoService.getDeviceName();
        const officeSelected = this.sessionContextService.getOfficeSelected<any>();
        const corpPrefix = this.sessionContextService.getCorpPrefix()
            || this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix)
            || '';
        const officeCd = officeSelected?.officeCd ?? '';
        const corpOffice = corpPrefix && officeCd ? `corp_${corpPrefix}#office_${officeCd}` : '';
        if (!deviceId || !settingConfig || !deviceName || !corpOffice) {
            console.warn('Setting config sync skipped: required data is missing.', {
                hasDeviceId: !!deviceId,
                hasSettingConfig: !!settingConfig,
                hasDeviceName: !!deviceName,
                hasCorpOffice: !!corpOffice,
            });
            return null;
        }

        return { deviceId, settingConfig, deviceName, corpOffice };
    }

    private getTvDeviceId(): string {
        const mode = this.localStorageService.getItem<string | number>(LOCAL_STORAGE_KEY.mode);
        return Number(mode) === Mode.TV
            ? String(this.firebaseDeviceInfoService.getAndroidDeviceId() ?? '').trim()
            : '';
    }

    private getMemoryTotalBytes(): number {
        try {
            const androidBridge = this.getDeviceLoginAndroidBridge();
            if (typeof androidBridge?.getDeviceMemory !== 'function') {
                return 0;
            }

            const memory = JSON.parse(androidBridge.getDeviceMemory());
            return Number(memory.totalBytes) || 0;
        } catch {
            return 0;
        }
    }

    private getDeviceLoginAndroidBridge(): DeviceLoginAndroidBridge | undefined {
        return (window as typeof window & { AndroidBridge?: DeviceLoginAndroidBridge }).AndroidBridge;
    }

}
